import { useState } from 'react';
import type { ProjectSearchResult } from '@/api/planning';
import type { AddKmLine } from '@/api/onderaannemersPoEdit';
import { AddLineModalShell } from './AddLineModalShell';
import { ProjectPicker } from './ProjectPicker';

interface AddKmModalProps {
  onAdd: (payload: AddKmLine) => Promise<void>;
  onClose: () => void;
}

/**
 * Add a "Kilometervergoeding onderaannemer" line. The €/km rate is server-authoritative,
 * so we only collect the number of km here.
 */
export function AddKmModal({ onAdd, onClose }: AddKmModalProps) {
  const [project, setProject] = useState<ProjectSearchResult | null>(null);
  const [km, setKm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedKm = Number(km.replace(',', '.'));
  const kmOk = Number.isFinite(parsedKm) && parsedKm > 0;
  const canSubmit = !!project && kmOk;

  const handleSubmit = async () => {
    if (!canSubmit || !project || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAdd({ kind: 'km', projectId: project.id, km: Math.round(parsedKm * 100) / 100 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kilometervergoeding toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <AddLineModalShell
      title="Kilometervergoeding toevoegen"
      submitLabel="Toevoegen"
      submitting={submitting}
      canSubmit={canSubmit}
      error={error}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div>
        <label className="block text-sm font-semibold text-td-navy mb-1">Project</label>
        <ProjectPicker selected={project} onSelect={setProject} autoFocus />
      </div>
      <div>
        <label htmlFor="add-km-value" className="block text-sm font-semibold text-td-navy mb-1">
          Aantal kilometer
        </label>
        <input
          id="add-km-value"
          type="text"
          inputMode="decimal"
          value={km}
          onChange={(e) => setKm(e.target.value)}
          placeholder="0"
          className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
        />
      </div>
      <p className="text-xs text-td-muted">Het tarief per km wordt door de server bepaald.</p>
    </AddLineModalShell>
  );
}
